import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { getBrowserName, openURL } from "@mirage/service-platform-actions";
import { CHROME_EXTENSION_WEB_STORE } from "@mirage/shared/urls";
import {
  browserNameForCurrentBrowser,
  extensionLinkForBrowser,
  openExtensionLinkForCurrentBrowser,
} from "@mirage/shared/util/webExtensionsHelpers";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useCallback, useEffect, useState } from "react";

const availableExtensionsAtom = atomWithStorage<string[]>(
  "availableExtensionsAtom",
  [],
);
export const AVAILABLE_BROWSER_EXTENSIONS = ["Chrome", "Edge"];

export default function useAvailableExtensions() {
  const isDesktop = EnvCtx.surface === "desktop";
  const [available, setAvailable] = useState(true);
  const [extensions, setExtensions] = useAtom(availableExtensionsAtom);
  const safariExtensionEnabled = convertFeatureValueToBool(
    useFeatureFlagValue("dash_2024_10_09_show_safari"),
  );

  useEffect(() => {
    if (safariExtensionEnabled) {
      setExtensions([...AVAILABLE_BROWSER_EXTENSIONS, "Safari"]);
    } else {
      setExtensions(AVAILABLE_BROWSER_EXTENSIONS);
    }
  }, [safariExtensionEnabled, setExtensions]);

  useEffect(() => {
    if (isDesktop) {
      // Desktop should always try to plug browser extension
      setAvailable(true);
    } else {
      const browser = browserNameForCurrentBrowser();
      setAvailable(extensions.includes(browser));
    }
  }, [extensions]);

  const openExtensionUrl = useCallback(async () => {
    if (EnvCtx.surface === "desktop") {
      const browser = await getBrowserName();
      if (browser) {
        if (extensions.map((e) => e.toLowerCase()).includes(browser)) {
          const extensionUrl = extensionLinkForBrowser(browser);
          openURL(extensionUrl);
        } else {
          openURL(CHROME_EXTENSION_WEB_STORE);
        }
      } else {
        openURL(CHROME_EXTENSION_WEB_STORE);
      }
      return;
    }

    openExtensionLinkForCurrentBrowser(openURL);
  }, [extensions]);

  return {
    extensions,
    extensionAvailableForBrowser: available,
    openExtensionUrl,
  };
}
